import React from "react";
import { Alert, Chip } from "@mui/material";
import { Check } from "@phosphor-icons/react";
import { useFormikContext } from "formik";

import LayerCard from "../elements/LayerCard";
import RadioCardGroup from "../../../shared/RadioCardGroup";
import type { L2Method, TargetFormValues } from "../TargetBuilderPanel.types";
import { useLModelCascade } from "../hooks/useLModelCascade";
import L2RatingScaleConfig from "./L2RatingScaleConfig";

const METHODS_WITH_CONFIG: L2Method[] = ["anchored-rating-scale"];

/**
 * L2 — Recording Method picker.
 * Options are filtered by current L1. Per the L-Model domain L1↔L2 is 1:1,
 * so each dimension exposes a single recommended recording method.
 * Selecting `anchored-rating-scale` renders an extra config card below.
 */
const L2MethodSelector: React.FC = () => {
  const { values } = useFormikContext<TargetFormValues>();
  const { l2Options, setL2 } = useLModelCascade();

  if (!values.l1_dimension) return null;

  const isSingleOption = l2Options.length === 1;
  const showRatingScaleConfig = values.l2_method === "anchored-rating-scale";

  return (
    <>
      <LayerCard
        title="How will you observe it in session?"
        description="Select how you will record this behavior during sessions"
      >
        <Alert
          severity="info"
          variant="standard"
          icon={isSingleOption ? <Check size={20} weight="bold" /> : undefined}
        >
          {isSingleOption
            ? "Based on your behavior type, there is one recommended recording method"
            : "Options are filtered by the behavioral dimension selected above."}
        </Alert>
        <RadioCardGroup<L2Method>
          ariaLabel="Recording method"
          value={values.l2_method}
          options={l2Options}
          onChange={setL2}
          renderRightSlot={(value, selected) =>
            selected && METHODS_WITH_CONFIG.includes(value) ? (
              <Chip size="small" variant="outlined" label="+ Config" />
            ) : null
          }
        />
      </LayerCard>

      {showRatingScaleConfig && (
        <LayerCard>
          <L2RatingScaleConfig />
        </LayerCard>
      )}
    </>
  );
};

export default L2MethodSelector;
